import { useMemo, useRef, useState } from 'react'
import {
  Search, Upload, Group, Unlink2, X, Link2, PanelsTopLeft,
  ListFilter, FileText, Activity, User, Calendar, ChevronRight,
} from 'lucide-react'
import { IntuiInput } from '@/components/intueri/IntuiInput'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import {
  IntuiDropdownMenu, IntuiDropdownMenuTrigger, IntuiDropdownMenuContent,
  IntuiDropdownMenuItem, IntuiDropdownMenuSub,
  IntuiDropdownMenuSubTrigger, IntuiDropdownMenuSubContent,
} from '@/components/intueri/IntuiDropdownMenu'
import { useTranslation } from '@/i18n'
import { cn } from '@/lib/utils'
import { filterUploaderOptions } from './uploaderOptions'
import { STATUS_META } from './statusMeta'

/**
 * Creation-date relative filter options. Matches the Figma submenu under
 * "Creation Date" (13360:79297). The value is a number of days back from
 * `now`; the row decides cutoff = Date.now() - value*24h.
 */
// `label` holds an i18n key resolved with t() at render time (FS-532).
const DATE_RANGE_OPTIONS: { value: string; label: string; days: number }[] = [
  { value: '1d',  label: 'data_catalog:filter.date_range.day_ago',         days: 1 },
  { value: '3d',  label: 'data_catalog:filter.date_range.days_ago',        days: 3 },
  { value: '1w',  label: 'data_catalog:filter.date_range.week_ago',        days: 7 },
  { value: '1m',  label: 'data_catalog:filter.date_range.month_ago',       days: 30 },
  { value: '3m',  label: 'data_catalog:filter.date_range.months_ago',      days: 90 },
  { value: '6m',  label: 'data_catalog:filter.date_range.six_months_ago',  days: 180 },
  { value: '1y',  label: 'data_catalog:filter.date_range.year_ago',        days: 365 },
]

/**
 * Public lookup of the same range keys → days. Exported so the parent
 * page can convert the selected key into an ISO cutoff for the server
 * filter without duplicating the labels list.
 */
export const DATE_RANGE_DAYS: Record<string, number> = Object.fromEntries(
  DATE_RANGE_OPTIONS.map((o) => [o.value, o.days])
)

/**
 * Status filter options. Each row gets a colored dot per Figma 13360:76959.
 * Values are the DocumentStatus enum keys (mapping is done in the parent).
 *
 * Order and palette come from STATUS_META so this and the stats banner cannot
 * drift apart again (UI-26).
 */
const STATUS_OPTIONS: { value: string; label: string; dotClass: string }[] =
  STATUS_META.map((s) => ({ value: s.value, label: s.labelKey, dotClass: s.dotClass }))

interface CatalogFilterBarProps {
  searchTerm: string
  onSearchChange: (value: string) => void
  selectedType: string
  onTypeChange: (value: string) => void
  filterTypes: string[]
  getDocTypeLabel: (type: string) => string
  selectedUploader: string
  onUploaderChange: (value: string) => void
  uploaders: [string, string][]
  /** NEW per Figma 13331:47890 — Status filter. 'all' = no filter. */
  selectedStatus?: string
  onStatusChange?: (value: string) => void
  /** NEW per Figma — Creation Date relative range. 'all' = no filter. */
  selectedCreationDate?: string
  onCreationDateChange?: (value: string) => void
  onUploadClick: () => void
  isUploadEnabled: boolean
  selectedCount?: number
  canMergeAsSet?: boolean
  onMergeAsSet?: () => void
  canUngroupSelected?: boolean
  onBulkUngroup?: () => void
  canMatchEntity?: boolean
  onMatchEntity?: () => void
  onOpenInNewBoard?: () => void
  /**
   * Board creation is in flight (it hydrates selections the current filter
   * hides, then POSTs). Shows a spinner and blocks a second click, which would
   * create a duplicate board (FS-625).
   */
  openInNewBoardBusy?: boolean
  onBulkDelete?: () => void
  onClearSelection?: () => void
  /**
   * Extends/overrides the root classes. The bar defaults to the full-page
   * catalog's `px-6`; modal embeds whose table container has no horizontal
   * padding pass `px-0` so the search input sits flush with the table edge.
   */
  className?: string
}

export default function CatalogFilterBar({
  searchTerm, onSearchChange,
  selectedType, onTypeChange, filterTypes, getDocTypeLabel,
  selectedUploader, onUploaderChange, uploaders,
  selectedStatus = 'all', onStatusChange,
  selectedCreationDate = 'all', onCreationDateChange,
  onUploadClick, isUploadEnabled,
  selectedCount = 0, canMergeAsSet, onMergeAsSet, canUngroupSelected, onBulkUngroup, canMatchEntity, onMatchEntity, onOpenInNewBoard, openInNewBoardBusy = false, onBulkDelete, onClearSelection,
  className,
}: CatalogFilterBarProps) {
  const { t } = useTranslation()

  // Free-text narrowing for the uploader list (UI-63) — the reporter asked for
  // "an open typing/search field" instead of scrolling a plain list.
  const [uploaderQuery, setUploaderQuery] = useState('')
  const [uploaderMenuOpen, setUploaderMenuOpen] = useState(false)
  const uploaderListRef = useRef<HTMLDivElement>(null)
  const filteredUploaders = useMemo(
    () => filterUploaderOptions(uploaders, uploaderQuery),
    [uploaders, uploaderQuery]
  )

  // Active filter chips — rendered below the search row when ≥1 narrower
  // than its default. Each chip has a × to remove just that filter; a
  // "Clear all" link sits at the row's right edge to drop them all at once.
  const activeChips = useMemo(() => {
    const chips: {
      key: string
      label: string
      dotClass?: string
      clear: () => void
    }[] = []
    if (selectedType && selectedType !== 'all') {
      chips.push({
        key: 'type',
        label: getDocTypeLabel(selectedType),
        clear: () => onTypeChange('all'),
      })
    }
    if (selectedStatus && selectedStatus !== 'all' && onStatusChange) {
      const opt = STATUS_OPTIONS.find((o) => o.value === selectedStatus)
      chips.push({
        key: 'status',
        label: opt ? t(opt.label as never) : selectedStatus,
        dotClass: opt?.dotClass,
        clear: () => onStatusChange('all'),
      })
    }
    if (selectedUploader && selectedUploader !== 'all') {
      const name = uploaders.find(([id]) => id === selectedUploader)?.[1] ?? selectedUploader
      chips.push({
        key: 'uploader',
        label: name,
        clear: () => onUploaderChange('all'),
      })
    }
    if (selectedCreationDate && selectedCreationDate !== 'all' && onCreationDateChange) {
      const opt = DATE_RANGE_OPTIONS.find((o) => o.value === selectedCreationDate)
      chips.push({
        key: 'creation_date',
        label: opt ? t(opt.label as never) : selectedCreationDate,
        clear: () => onCreationDateChange('all'),
      })
    }
    return chips
  }, [
    selectedType, selectedStatus, selectedUploader, selectedCreationDate,
    getDocTypeLabel, uploaders, onTypeChange, onStatusChange, onUploaderChange, onCreationDateChange, t,
  ])
  const hasActiveFilters = activeChips.length > 0

  const clearAllFilters = () => {
    onTypeChange('all')
    onStatusChange?.('all')
    onUploaderChange('all')
    onCreationDateChange?.('all')
  }

  return (
    <div className={cn('flex flex-col px-6 py-4 gap-3', className)}>
      {/* Wraps at narrow widths. The right group uses ml-auto (not
          justify-between) so it stays right-aligned even once wrapped. */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="relative w-[240px]">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <IntuiInput
              placeholder={t('data_catalog:search_placeholder', 'Search')}
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="h-9 ps-9"
            />
          </div>
          {/* Filter icon — opens a cascading dropdown menu per Figma
              13331:47890. Each top-level item opens a panel to the right with
              its values (Document Type / Status / Uploader / Date). */}
          <IntuiDropdownMenu
            onOpenChange={(open) => {
              // Closing the Filters menu closes the uploader panel with it, and
              // drops its query so reopening never shows a list silently
              // narrowed by a forgotten search.
              if (!open) {
                setUploaderMenuOpen(false)
                setUploaderQuery('')
              }
            }}
          >
            {/* UI-21: same shape as the other two filter triggers in the app
                (TagMappingFilterMenu, TagDetail) — outline + size lg + the
                ListFilter glyph + a visible label. This one was icon-only,
                which also read as "button icon too subtle / gone". The label
                supplies the accessible name, so aria-label/title are gone with
                it rather than competing with the visible text. */}
            <IntuiDropdownMenuTrigger asChild>
              <IntuiButton
                variant="outline"
                size="lg"
                active={hasActiveFilters}
              >
                <ListFilter className="size-4" />
                {t('data_catalog:filter.button', { defaultValue: 'Filters' })}
              </IntuiButton>
            </IntuiDropdownMenuTrigger>
            {/* z-[1000000] keeps the menu above modal dialogs (z-[999999]) when
                this bar is reused inside a Dialog, e.g. the board Add Files modal (FS-534). */}
            <IntuiDropdownMenuContent align="start" sideOffset={8} className="z-[1000000] w-[200px]">
              <div className="px-2 py-1 text-xs font-medium text-muted-foreground">
                {t('data_catalog:filter.title', { defaultValue: 'Filter' })}
              </div>

              {/* Document Type */}
              <IntuiDropdownMenuSub>
                <IntuiDropdownMenuSubTrigger>
                  <FileText className="size-4 mr-2 text-muted-foreground" />
                  {t('data_catalog:filter.document_type', { defaultValue: 'Document Type' })}
                </IntuiDropdownMenuSubTrigger>
                <IntuiDropdownMenuSubContent className="z-[1000000] w-[180px]">
                  {filterTypes.map((type) => (
                    <IntuiDropdownMenuItem
                      key={type}
                      onSelect={() => onTypeChange(type)}
                      className={selectedType === type ? 'bg-accent' : ''}
                    >
                      {type === 'all' ? t('data_catalog:all_types', 'All File Types') : getDocTypeLabel(type)}
                    </IntuiDropdownMenuItem>
                  ))}
                </IntuiDropdownMenuSubContent>
              </IntuiDropdownMenuSub>

              {/* Status */}
              {onStatusChange && (
                <IntuiDropdownMenuSub>
                  <IntuiDropdownMenuSubTrigger>
                    <Activity className="size-4 mr-2 text-muted-foreground" />
                    {t('data_catalog:filter.status', { defaultValue: 'Status' })}
                  </IntuiDropdownMenuSubTrigger>
                  <IntuiDropdownMenuSubContent className="z-[1000000] w-[180px]">
                    <IntuiDropdownMenuItem
                      onSelect={() => onStatusChange('all')}
                      className={selectedStatus === 'all' ? 'bg-accent' : ''}
                    >
                      {t('data_catalog:filter.any_status', { defaultValue: 'Any' })}
                    </IntuiDropdownMenuItem>
                    {STATUS_OPTIONS.map((opt) => (
                      <IntuiDropdownMenuItem
                        key={opt.value}
                        onSelect={() => onStatusChange(opt.value)}
                        className={selectedStatus === opt.value ? 'bg-accent' : ''}
                      >
                        <span className={`size-2 rounded-full mr-2 ${opt.dotClass}`} />
                        {t(opt.label as never)}
                      </IntuiDropdownMenuItem>
                    ))}
                  </IntuiDropdownMenuSubContent>
                </IntuiDropdownMenuSub>
              )}

              {/*
                Uploader Name (UI-63).

                A side panel that reads as the submenu it replaces, but is a
                nested menu rather than a Radix `Sub`: a text field cannot hold
                focus inside a `MenuSubContent` — the submenu pulls focus back,
                so keystrokes reach the menu typeahead instead of the input and
                the panel closes. Top-level menu content has no such behaviour.
                Verified against both structures in CatalogFilterBar.uploaderSearch.

                Popover + IntuiCommand (the shadcn Combobox, both halves of which
                this repo ships) also holds focus and would supply the search,
                matching and arrow-key navigation for free. It was weighed and
                not taken: this row has to read as one of four identical filter
                rows opening the same panel, and a Popover carries different
                styling and no menu semantics.
              */}
              <IntuiDropdownMenu
                open={uploaderMenuOpen}
                // Left modal (the default) deliberately. As a second root
                // portalled to body it looked like the outer layer might treat a
                // pointerdown inside it as an outside-click, or aria-hide it as a
                // body sibling; measured, neither happens, and `modal={false}`
                // actively breaks it — the outer scope then reclaims focus from
                // the field and the panel closes on the first keystroke. The
                // uploaderSearch specs pin both halves by role.
                onOpenChange={(open) => {
                  setUploaderMenuOpen(open)
                  if (!open) setUploaderQuery('')
                }}
              >
                <IntuiDropdownMenuTrigger asChild>
                  <IntuiDropdownMenuItem
                    onSelect={(e) => e.preventDefault()}
                    className="data-[state=open]:bg-accent"
                  >
                    <User className="size-4 mr-2 text-muted-foreground" />
                    {t('data_catalog:filter.uploader', { defaultValue: 'Uploader Name' })}
                    <ChevronRight className="ml-auto" />
                  </IntuiDropdownMenuItem>
                </IntuiDropdownMenuTrigger>
                <IntuiDropdownMenuContent
                  side="right"
                  align="start"
                  sideOffset={4}
                  className="z-[1000000] w-[220px]"
                >
                  <div className="p-1">
                    <IntuiInput
                      value={uploaderQuery}
                      onChange={(e) => setUploaderQuery(e.target.value)}
                      onKeyDown={(e) => {
                        // Radix moves roving focus only for keys landing on the
                        // content itself, so ArrowDown from the field is ignored
                        // and the filtered list would be mouse-only. Hand the
                        // first row focus explicitly.
                        if (e.key === 'ArrowDown') {
                          e.preventDefault()
                          uploaderListRef.current
                            ?.querySelector<HTMLElement>('[role="menuitem"]')
                            ?.focus()
                          return
                        }
                        // Suppress only the printable keys the menu typeahead
                        // would steal — a blanket stopPropagation also swallows
                        // Home/End and Tab.
                        if (e.key.length === 1) e.stopPropagation()
                      }}
                      placeholder={t('data_catalog:filter.uploader_search', {
                        defaultValue: 'Search uploaders',
                      })}
                      aria-label={t('data_catalog:filter.uploader_search', {
                        defaultValue: 'Search uploaders',
                      })}
                      className="h-8"
                    />
                  </div>
                  <div ref={uploaderListRef} className="max-h-[240px] overflow-y-auto">
                    <IntuiDropdownMenuItem
                      onSelect={() => onUploaderChange('all')}
                      className={selectedUploader === 'all' ? 'bg-accent' : ''}
                      aria-label={t('data_catalog:filter.any_uploader_a11y', {
                        defaultValue: 'Any uploader',
                      })}
                    >
                      {t('data_catalog:any_uploader', 'Any')}
                    </IntuiDropdownMenuItem>
                    {filteredUploaders.map(([userId, name]) => (
                      <IntuiDropdownMenuItem
                        key={userId}
                        onSelect={() => onUploaderChange(userId)}
                        className={selectedUploader === userId ? 'bg-accent' : ''}
                      >
                        <User className="size-4 mr-2 text-muted-foreground" />
                        {name}
                      </IntuiDropdownMenuItem>
                    ))}
                    {filteredUploaders.length === 0 && (
                      <p className="px-2 py-1.5 text-sm text-muted-foreground">
                        {t('data_catalog:filter.uploader_no_matches', {
                          defaultValue: 'No matching uploaders',
                        })}
                      </p>
                    )}
                  </div>
                </IntuiDropdownMenuContent>
              </IntuiDropdownMenu>

              {/* Creation Date */}
              {onCreationDateChange && (
                <IntuiDropdownMenuSub>
                  <IntuiDropdownMenuSubTrigger>
                    <Calendar className="size-4 mr-2 text-muted-foreground" />
                    {t('data_catalog:filter.creation_date', { defaultValue: 'Creation Date' })}
                  </IntuiDropdownMenuSubTrigger>
                  <IntuiDropdownMenuSubContent className="z-[1000000] w-[160px]">
                    <IntuiDropdownMenuItem
                      onSelect={() => onCreationDateChange('all')}
                      className={selectedCreationDate === 'all' ? 'bg-accent' : ''}
                    >
                      {t('data_catalog:filter.any_date', { defaultValue: 'Any time' })}
                    </IntuiDropdownMenuItem>
                    {DATE_RANGE_OPTIONS.map((opt) => (
                      <IntuiDropdownMenuItem
                        key={opt.value}
                        onSelect={() => onCreationDateChange(opt.value)}
                        className={selectedCreationDate === opt.value ? 'bg-accent' : ''}
                      >
                        {t(opt.label as never)}
                      </IntuiDropdownMenuItem>
                    ))}
                  </IntuiDropdownMenuSubContent>
                </IntuiDropdownMenuSub>
              )}
            </IntuiDropdownMenuContent>
          </IntuiDropdownMenu>
        </div>
        {selectedCount > 0 ? (
          <div className="flex flex-wrap items-center justify-end gap-3 ml-auto">
            {canMergeAsSet && (
              <IntuiButton variant="outline" className="h-9" onClick={onMergeAsSet}>
                <Group className="size-4 mr-1.5" />
                {t('data_catalog:filter_bar.merge_as_set', { count: selectedCount })}
              </IntuiButton>
            )}
            {canUngroupSelected && (
              <IntuiButton variant="outline" className="h-9" onClick={onBulkUngroup}>
                <Unlink2 className="size-4 mr-1.5" />
                {t('data_catalog:filter_bar.ungroup')}
              </IntuiButton>
            )}
            {onOpenInNewBoard && (
              <IntuiButton
                variant="outline"
                className="h-9"
                onClick={onOpenInNewBoard}
                loading={openInNewBoardBusy}
              >
                {!openInNewBoardBusy && <PanelsTopLeft className="size-4 mr-1.5" />}
                {t('data_catalog:open_in_new_board.button', {
                  defaultValue: 'Open in new board ({{count}})',
                  count: selectedCount,
                })}
              </IntuiButton>
            )}
            {canMatchEntity && onMatchEntity && (
              <IntuiButton variant="outline" className="h-9" onClick={onMatchEntity}>
                <Link2 className="size-4 mr-1.5" />
                {t('data_catalog:match_entity.button', {
                  defaultValue: 'Match Entity ({{count}})',
                  count: selectedCount,
                })}
              </IntuiButton>
            )}
            {onBulkDelete && (
              <button
                type="button"
                onClick={onBulkDelete}
                className="inline-flex items-center justify-center h-9 px-2.5 text-sm font-medium rounded-md bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors"
              >
                {t('data_catalog:filter_bar.delete_count', { count: selectedCount })}
              </button>
            )}
            <IntuiButton variant="outline" size="icon" className="size-9" onClick={onClearSelection}>
              <X className="size-4" />
            </IntuiButton>
          </div>
        ) : isUploadEnabled ? (
          // data-tour-id: help-agent "upload-data" guided-tour anchor
          // (components/HelpAgent/TourOverlay/tourRegistry.ts).
          <IntuiButton onClick={onUploadClick} className="h-9 ml-auto" data-tour-id="data-catalog-upload">
            {/* No mr-1.5 — IntuiButton's base gap-1.5 spaces icon/label. */}
            <Upload className="size-4" />
            {t('common:buttons.upload')}
          </IntuiButton>
        ) : null}
      </div>

      {/* Row 2 — active filter chips per Figma 13360:80434 ("6. Filter
          selected"). Light gray band, rounded-full pills with thin
          border. Status chip carries its colored dot (green/orange/red);
          other chips have no leading glyph. "Clear All Filters" sits at
          the row's right edge. Only rendered when ≥1 filter narrower
          than its default. */}
      {hasActiveFilters && (
        <div className="flex items-center justify-between gap-3 rounded-md bg-muted/40 px-3 py-2 dark:bg-dark-accent/40">
          <div className="flex flex-wrap items-center gap-2">
            {activeChips.map((chip) => (
              <span
                key={chip.key}
                className="inline-flex items-center gap-1.5 max-w-[260px] rounded-full border border-border bg-background pl-2.5 pr-1.5 py-1 text-xs font-medium text-foreground"
              >
                {chip.dotClass && (
                  <span aria-hidden className={`size-2 shrink-0 rounded-full ${chip.dotClass}`} />
                )}
                <span className="truncate">{chip.label}</span>
                <button
                  type="button"
                  onClick={chip.clear}
                  aria-label={t('data_catalog:filter.remove_chip', {
                    defaultValue: 'Remove {{label}} filter',
                    label: chip.label,
                  })}
                  className="shrink-0 grid place-items-center w-4 h-4 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <X className="size-3" />
                </button>
              </span>
            ))}
          </div>
          <button
            type="button"
            onClick={clearAllFilters}
            className="shrink-0 text-xs text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
          >
            {t('data_catalog:filter.clear_all', { defaultValue: 'Clear All Filters' })}
          </button>
        </div>
      )}
    </div>
  )
}
